import { useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { useParams, Link } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Camp, PricingItem } from '../types'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { STRIPE_PUBLISHABLE_KEY } from '../config/stripe'
import { useToast } from '../components/Toast'

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)

// Child schema
const childSchema = z.object({
  childFullName: z.string().min(2, 'Name is required'),
  childDob: z.string().min(1, 'Date of birth is required'),
  walkHomeAlone: z.enum(['yes', 'no'], { required_error: 'Required' }),
  hasMedicalConditions: z.enum(['yes', 'no'], { required_error: 'Required' }),
  medicalConditionsDetails: z.string().optional(),
  hasAdditionalNeeds: z.enum(['yes', 'no'], { required_error: 'Required' }),
  additionalNeedsDetails: z.string().optional(),
  hasAllergies: z.enum(['yes', 'no'], { required_error: 'Required' }),
  allergiesDetails: z.string().optional(),
  hasMedication: z.enum(['yes', 'no'], { required_error: 'Required' }),
  medicationDetails: z.string().optional(),
  hasFurtherInfo: z.enum(['yes', 'no'], { required_error: 'Required' }),
  furtherInfoDetails: z.string().optional(),
})

// Multi-child form schema
const multiChildFormSchema = z.object({
  email: z.string().email('Valid email required'),
  parentFullName: z.string().min(2, 'Parent name is required'),
  address: z.string().optional(),
  phone: z.string().min(10, 'Valid phone number required'),
  emergency1Name: z.string().min(2, 'Emergency contact required'),
  emergency1Phone: z.string().min(10, 'Valid phone required'),
  emergency1Relationship: z.string().min(1, 'Relationship required'),
  emergency2Name: z.string().optional(),
  emergency2Phone: z.string().optional(),
  emergency2Relationship: z.string().optional(),
  authorisedCollectors: z.string().optional(),
  children: z.array(childSchema).min(1, 'At least one child is required'),
  permissionPhotos: z.boolean().refine(v => v, 'Required'),
  permissionHealth: z.boolean().refine(v => v, 'Required'),
  permissionActivities: z.boolean().refine(v => v, 'Required'),
  permissionLocations: z.boolean().refine(v => v, 'Required'),
  permissionMeals: z.boolean().refine(v => v, 'Required'),
  permissionBathroom: z.boolean().refine(v => v, 'Required'),
  permissionFirstAid: z.boolean().refine(v => v, 'Required'),
  permissionEquipment: z.boolean().refine(v => v, 'Required'),
  permissionAppWaiver: z.boolean().refine(v => v, 'Required'),
})

type MultiChildFormData = z.infer<typeof multiChildFormSchema>
type ChildFormData = z.infer<typeof childSchema>

const defaultChild: ChildFormData = {
  childFullName: '',
  childDob: '',
  walkHomeAlone: 'no',
  hasMedicalConditions: 'no',
  medicalConditionsDetails: '',
  hasAdditionalNeeds: 'no',
  additionalNeedsDetails: '',
  hasAllergies: 'no',
  allergiesDetails: '',
  hasMedication: 'no',
  medicationDetails: '',
  hasFurtherInfo: 'no',
  furtherInfoDetails: '',
}

type UnavailableReason = 'not_found' | 'archived' | 'past' | 'paused' | 'closed' | 'full'

export default function RegistrationFormEnhanced() {
  const { campId } = useParams<{ campId: string }>()
  const [camps, setCamps] = useState<Camp[]>([])
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null)
  const [unavailableCamp, setUnavailableCamp] = useState<{ camp: Camp | null; reason: UnavailableReason } | null>(null)
  const [pricingItems, setPricingItems] = useState<PricingItem[]>([])
  const [selectedItems, setSelectedItems] = useState<{ [key: number]: boolean }>({})
  const [loading, setLoading] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [totalAmount, setTotalAmount] = useState(0)
  const [siblingDiscount, setSiblingDiscount] = useState(0)
  const [clientSecret, setClientSecret] = useState<string>('')
  const [step, setStep] = useState<'form' | 'payment'>('form')
  const { showToast } = useToast()

  // Load saved form data from localStorage
  const getSavedFormData = () => {
    try {
      const saved = localStorage.getItem('registration_form_draft')
      if (saved) {
        const parsed = JSON.parse(saved)
        // Check if saved data is less than 24 hours old
        if (parsed._savedAt && Date.now() - parsed._savedAt < 24 * 60 * 60 * 1000) {
          const { _savedAt, ...formData } = parsed
          return formData
        }
      }
    } catch (e) {
      console.error('Failed to load saved form data:', e)
    }
    return null
  }

  const savedData = getSavedFormData()

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<MultiChildFormData>({
    resolver: zodResolver(multiChildFormSchema),
    defaultValues: savedData || {
      children: [defaultChild],
      permissionPhotos: false,
      permissionHealth: false,
      permissionActivities: false,
      permissionLocations: false,
      permissionMeals: false,
      permissionBathroom: false,
      permissionFirstAid: false,
      permissionEquipment: false,
      permissionAppWaiver: false,
    },
  })

  // Auto-save form data to localStorage
  const formValues = watch()
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('registration_form_draft', JSON.stringify({
          ...formValues,
          _savedAt: Date.now()
        }))
      } catch (e) {
        // Ignore storage errors
      }
    }, 1000) // Debounce by 1 second
    return () => clearTimeout(timeoutId)
  }, [formValues])

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'children',
  })

  // const watchedChildren = watch('children')

  // Load camps and pricing
  useEffect(() => {
    const fetchUrls = [
      fetch('/api/camps').then(r => r.json()),
      fetch('/api/pricing').then(r => r.json()),
    ]
    // If accessing a specific camp, also fetch all camps to determine unavailable reason
    if (campId) {
      fetchUrls.push(fetch('/api/camps?includeArchived=true').then(r => r.json()))
    }
    
    Promise.all(fetchUrls)
      .then(([campsData, pricingData, allCampsData]) => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        
        // Filter to show only:
        // - Active camps (not archived/full)
        // - Camps that haven't ended yet
        // - Camps where registration is open
        const activeCamps = campsData.camps?.filter((c: Camp) => {
          const endDate = new Date(c.endDate)
          endDate.setHours(23, 59, 59, 999) // End of the last day
          
          return c.status === 'active' && 
                 endDate >= today && 
                 c.registrationStatus === 'open'
        }) || []
        setCamps(activeCamps)
        const allPricingItems = pricingData.items || []
        setPricingItems(allPricingItems)
        
        if (campId) {
          const camp = activeCamps.find((c: Camp) => c.id === parseInt(campId))
          if (camp) {
            // Camp is available for registration
            setSelectedCamp(camp)
            // Auto-select required items for this camp
            const requiredItems: { [key: number]: boolean } = {}
            allPricingItems
              .filter((item: PricingItem) => item.campId === camp.id && item.isRequired)
              .forEach((item: PricingItem) => { requiredItems[item.id] = true })
            setSelectedItems(requiredItems)
          } else {
            // Camp not in active list - determine why
            const allCamps = allCampsData?.camps || []
            const requestedCamp = allCamps.find((c: Camp) => c.id === parseInt(campId))
            
            if (!requestedCamp) {
              setUnavailableCamp({ camp: null, reason: 'not_found' })
            } else {
              const endDate = new Date(requestedCamp.endDate)
              endDate.setHours(23, 59, 59, 999)
              const spotsLeft = requestedCamp.maxSpots - requestedCamp.spotsTaken
              
              if (requestedCamp.status === 'archived') {
                setUnavailableCamp({ camp: requestedCamp, reason: 'archived' })
              } else if (endDate < today) {
                setUnavailableCamp({ camp: requestedCamp, reason: 'past' })
              } else if (requestedCamp.registrationStatus === 'paused') {
                setUnavailableCamp({ camp: requestedCamp, reason: 'paused' })
              } else if (requestedCamp.registrationStatus === 'closed') {
                setUnavailableCamp({ camp: requestedCamp, reason: 'closed' })
              } else if (spotsLeft <= 0 && !requestedCamp.waitlistEnabled) {
                setUnavailableCamp({ camp: requestedCamp, reason: 'full' })
              } else {
                setUnavailableCamp({ camp: requestedCamp, reason: 'not_found' })
              }
            }
          }
        }
        
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [campId])
  
  // Auto-select required items when camp changes
  useEffect(() => {
    if (selectedCamp) {
      const requiredItems: { [key: number]: boolean } = {}
      pricingItems
        .filter(item => item.campId === selectedCamp.id && item.isRequired)
        .forEach(item => { requiredItems[item.id] = true })
      // Merge with existing selections, keeping required items selected
      setSelectedItems(prev => ({
        ...Object.fromEntries(
          Object.entries(prev).filter(([id]) => 
            pricingItems.find(item => item.id === parseInt(id))?.campId === selectedCamp.id
          )
        ),
        ...requiredItems
      }))
    }
  }, [selectedCamp, pricingItems])

  // Calculate total - pricing per child with sibling discount
  useEffect(() => {
    if (!selectedCamp) {
      setTotalAmount(0)
      setSiblingDiscount(0)
      return
    }

    // Only show pricing items specifically assigned to this camp
    const campPricingItems = pricingItems.filter(
      item => item.campId === selectedCamp.id
    )

    let itemTotal = 0
    campPricingItems.forEach((item) => {
      if (selectedItems[item.id]) {
        itemTotal += item.amount
      }
    })

    // Multiply by number of children
    const numChildren = fields.length
    let total = itemTotal * numChildren
    
    // Calculate sibling discount for 2+ children
    let discount = 0
    if (numChildren >= 2 && selectedCamp.siblingDiscountEnabled) {
      const additionalChildren = numChildren - 1
      
      if (selectedCamp.siblingDiscountType === 'percentage') {
        discount = (itemTotal * (selectedCamp.siblingDiscountAmount / 100)) * additionalChildren
      } else {
        discount = selectedCamp.siblingDiscountAmount * additionalChildren
      }
    }
    
    setSiblingDiscount(discount)
    setTotalAmount(Math.max(total - discount, 0))
  }, [selectedCamp, selectedItems, pricingItems, fields.length])

  const onSubmitForm = async (data: MultiChildFormData) => {
    if (!selectedCamp) {
      showToast('Please select a camp', 'warning')
      return
    }

    // Check if enough spots available
    const spotsNeeded = data.children.length
    const spotsLeft = selectedCamp.maxSpots - selectedCamp.spotsTaken
    if (spotsNeeded > spotsLeft && !selectedCamp.waitlistEnabled) {
      showToast(`Only ${spotsLeft} spots available, but you're registering ${spotsNeeded} children.`, 'error')
      return
    }

    try {
      // Check if this is a waitlist submission
      const isWaitlist = selectedCamp.maxSpots - selectedCamp.spotsTaken <= 0 && selectedCamp.waitlistEnabled
      
      // If total is 0 (free camp) or waitlist, skip payment and submit directly
      if (totalAmount === 0 || isWaitlist) {
        const response = await fetch('/api/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...data,
            campId: selectedCamp.id,
            selectedItems: Object.keys(selectedItems).filter(k => selectedItems[parseInt(k)]).map(k => parseInt(k)),
            totalAmount: isWaitlist ? 0 : 0,
            paymentStatus: isWaitlist ? 'pending' : 'free',
            registrationStatus: isWaitlist ? 'waitlist' : 'confirmed',
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Registration failed')
        }

        localStorage.removeItem('registration_form_draft')
        setSubmitted(true)
        return
      }

      // Otherwise, create payment intent
      const selectedItemIds = Object.keys(selectedItems)
        .filter(k => selectedItems[parseInt(k)])
        .map(k => parseInt(k))
      
      const paymentRes = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campId: selectedCamp.id,
          selectedItems: selectedItemIds,
          email: data.email,
          childrenCount: data.children.length,
        }),
      })

      const paymentData = await paymentRes.json()

      if (!paymentRes.ok) {
        throw new Error(paymentData.error || 'Failed to create payment')
      }

      // If somehow free (e.g., discount made it free), submit directly
      if (paymentData.isFree || !paymentData.clientSecret) {
        const response = await fetch('/api/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...data,
            campId: selectedCamp.id,
            selectedItems: selectedItemIds,
            totalAmount: 0,
            paymentStatus: 'free',
          }),
        })
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Registration failed')
        }
        localStorage.removeItem('registration_form_draft')
        setSubmitted(true)
        return
      }

      setClientSecret(paymentData.clientSecret)
      
      sessionStorage.setItem('registration_form_data', JSON.stringify({
        ...data,
        campId: selectedCamp.id,
        selectedItems: Object.keys(selectedItems).filter(k => selectedItems[parseInt(k)]).map(k => parseInt(k)),
        totalAmount,
      }))

      setStep('payment')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to process registration', 'error')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="dark:text-white light:text-gray-900 text-xl">Loading camps...</div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="card max-w-2xl mx-auto text-center">
        <div className="text-6xl mb-4">✅</div>
        <h2 className="font-heading text-3xl font-bold dark:text-white light:text-gray-900 uppercase mb-4">
          Registration Complete!
        </h2>
        <p className="dark:text-gray-300 light:text-gray-700 mb-4">
          Thank you for registering {fields.length} {fields.length === 1 ? 'child' : 'children'}! 
          A confirmation email has been sent.
        </p>
        <p className="dark:text-gray-400 light:text-gray-600 text-sm">
          Payment confirmation: AED {totalAmount.toFixed(2)} paid
        </p>
      </div>
    )
  }

  if (step === 'payment' && clientSecret) {
    return (
      <Elements stripe={stripePromise} options={{ clientSecret }}>
        <PaymentForm 
          totalAmount={totalAmount}
          childCount={fields.length}
          onSuccess={() => {
            localStorage.removeItem('registration_form_draft')
            setSubmitted(true)
          }}
          onBack={() => setStep('form')}
        />
      </Elements>
    )
  }

  // If a campId was provided but camp is unavailable - show specific messaging
  if (campId && !selectedCamp && unavailableCamp) {
    const { camp, reason } = unavailableCamp
    
    const messages: Record<UnavailableReason, { icon: string; title: string; message: string }> = {
      not_found: {
        icon: '❓',
        title: 'Camp Not Found',
        message: 'The camp you\'re looking for doesn\'t exist or has been removed.',
      },
      archived: {
        icon: '📦',
        title: 'Camp Archived',
        message: 'This camp has been archived and is no longer available for registration.',
      },
      past: {
        icon: '📅',
        title: 'Camp Has Ended',
        message: 'This camp has already finished. Check out our upcoming camps!',
      },
      paused: {
        icon: '⏸️',
        title: 'Registration Paused',
        message: camp?.waitlistMessage || 'Registration for this camp is temporarily paused. Please check back later.',
      },
      closed: {
        icon: '🔒',
        title: 'Registration Closed',
        message: 'Registration for this camp is now closed.',
      },
      full: {
        icon: '🎟️',
        title: 'Camp Fully Booked',
        message: 'This camp is fully booked and no longer accepting registrations.',
      },
    }
    
    const { icon, title, message } = messages[reason]
    
    return (
      <div className="card max-w-2xl mx-auto text-center">
        <div className="text-6xl mb-4">{icon}</div>
        <h2 className="font-heading text-2xl font-bold dark:text-white light:text-gray-900 uppercase mb-4">
          {title}
        </h2>
        {camp && (
          <div className="mb-4 p-4 dark:bg-gray-800/50 light:bg-gray-100 rounded border dark:border-gray-700 light:border-gray-300">
            <h3 className="font-heading text-lg text-brand-primary mb-1">{camp.name}</h3>
            <p className="dark:text-gray-500 light:text-gray-500 text-sm">
              📅 {camp.startDate} - {camp.endDate}
            </p>
          </div>
        )}
        <p className="dark:text-gray-400 light:text-gray-600 mb-6">
          {message}
        </p>
        <Link to="/" className="btn-primary inline-block">
          View Available Camps
        </Link>
      </div>
    )
  }

  if (camps.length === 0) {
    return (
      <div className="card max-w-2xl mx-auto text-center">
        <h2 className="font-heading text-2xl font-bold dark:text-white light:text-gray-900 uppercase mb-4">
          No Active Camps
        </h2>
        <p className="dark:text-gray-400 light:text-gray-600">
          There are currently no camps available for registration.
        </p>
      </div>
    )
  }

  // Only show pricing items specifically assigned to this camp
  const campPricingItems = selectedCamp 
    ? pricingItems.filter(item => item.campId === selectedCamp.id)
    : []

  return (
    <div className="max-w-3xl mx-auto">
      {/* Camp Selection */}
      {!campId && (
        <div className="card mb-6">
          <h2 className="font-heading text-2xl font-bold dark:text-white light:text-gray-900 uppercase mb-4">
            Select a Camp
          </h2>
          <div className="space-y-3">
            {camps.map((camp) => {
              const spotsLeft = camp.maxSpots - camp.spotsTaken
              const isFull = spotsLeft <= 0

              // Hide full camps unless waitlist is enabled
              if (isFull && !camp.waitlistEnabled) return null

              return (
                <Link
                  key={camp.id}
                  to={`/camp/${camp.id}`}
                  className="block w-full text-left p-4 rounded border-2 transition-all dark:border-gray-700 light:border-gray-300 hover:border-brand-primary/50"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-heading text-lg font-bold dark:text-white light:text-gray-900">{camp.name}</h3>
                    <div className="flex flex-col items-end gap-1">
                      {isFull && camp.waitlistEnabled ? (
                        <span className="text-yellow-400 text-sm font-bold">📋 WAITLIST</span>
                      ) : (
                        <span className="text-green-400 text-sm">{spotsLeft} spots left</span>
                      )}
                    </div>
                  </div>
                  <p className="dark:text-gray-400 light:text-gray-600 text-sm mb-2">{camp.description}</p>
                  <div className="flex gap-4 text-xs dark:text-gray-500 light:text-gray-500">
                    <span>📅 {camp.startDate} - {camp.endDate}</span>
                    <span>👥 Ages {camp.ageMin}-{camp.ageMax}</span>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* Camp Info Banner */}
      {campId && selectedCamp && (
        <div className="card mb-6 bg-brand-primary/10 border-brand-primary">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="font-heading text-2xl font-bold dark:text-white light:text-gray-900 mb-2">{selectedCamp.name}</h2>
              <p className="dark:text-gray-300 light:text-gray-700 text-sm mb-2">{selectedCamp.description}</p>
              <div className="flex flex-wrap gap-4 text-sm dark:text-gray-400 light:text-gray-600">
                <span>📅 {selectedCamp.startDate} - {selectedCamp.endDate}</span>
                <span>👥 Ages {selectedCamp.ageMin}-{selectedCamp.ageMax}</span>
                {selectedCamp.registrationStatus === 'open' && (
                  <span className="text-green-400">{selectedCamp.maxSpots - selectedCamp.spotsTaken} spots left</span>
                )}
              </div>
              {selectedCamp.siblingDiscountEnabled && selectedCamp.registrationStatus === 'open' && (
                <div className="mt-3 flex items-center gap-2 px-4 py-2 dark:bg-green-900/30 light:bg-green-50 border dark:border-green-700 light:border-green-300 rounded-lg text-sm dark:text-green-400 light:text-green-700">
                  🎉 Sibling discount available: 
                  {selectedCamp.siblingDiscountType === 'percentage' 
                    ? ` ${selectedCamp.siblingDiscountAmount}% off`
                    : ` AED ${selectedCamp.siblingDiscountAmount} off`
                  } per additional child
                </div>
              )}
              
              {/* Registration Status Messages */}
              {selectedCamp.registrationStatus === 'paused' && (
                <div className="mt-4 p-4 dark:bg-yellow-900/30 light:bg-yellow-50 border dark:border-yellow-700 light:border-yellow-300 rounded-lg">
                  <div className="flex items-center gap-2 dark:text-yellow-400 light:text-yellow-700 font-bold mb-1">
                    ⏸️ Registration Paused
                  </div>
                  <p className="dark:text-yellow-200/80 light:text-yellow-600 text-sm">
                    Registration for this camp is temporarily paused. Please check back later or contact us for more information.
                  </p>
                </div>
              )}
              
              {selectedCamp.registrationStatus === 'closed' && (
                <div className="mt-4 p-4 dark:bg-red-900/30 light:bg-red-50 border dark:border-red-700 light:border-red-300 rounded-lg">
                  <div className="flex items-center gap-2 dark:text-red-400 light:text-red-700 font-bold mb-1">
                    🔒 Registration Closed
                  </div>
                  <p className="dark:text-red-200/80 light:text-red-600 text-sm">
                    Registration for this camp is now closed.
                  </p>
                </div>
              )}
              
              {selectedCamp.registrationStatus === 'open' && selectedCamp.maxSpots - selectedCamp.spotsTaken <= 0 && selectedCamp.waitlistEnabled && (
                <div className="mt-4 p-4 dark:bg-purple-900/30 light:bg-purple-50 border dark:border-purple-700 light:border-purple-300 rounded-lg">
                  <div className="flex items-center gap-2 dark:text-purple-400 light:text-purple-700 font-bold mb-1">
                    📋 Join Waitlist
                  </div>
                  <p className="dark:text-purple-200/80 light:text-purple-600 text-sm">
                    This camp is currently full, but you can join the waitlist. {selectedCamp.waitlistMessage || "We'll contact you if a spot opens up."}
                  </p>
                </div>
              )}
            </div>
            <Link to="/" className="text-brand-primary hover:underline text-sm whitespace-nowrap">
              ← View all camps
            </Link>
          </div>
        </div>
      )}

      {selectedCamp && (
        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
          
          {/* Children Section */}
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-2xl font-bold dark:text-white light:text-gray-900 uppercase">
                Children ({fields.length})
              </h2>
              <button
                type="button"
                onClick={() => append(defaultChild)}
                className="btn-secondary text-sm"
              >
                + Add Another Child
              </button>
            </div>
            
            {fields.map((field, index) => (
              <ChildSection
                key={field.id}
                index={index}
                register={register}
                errors={errors}
                watch={watch}
                onRemove={() => remove(index)}
                canRemove={fields.length > 1}
              />
            ))}
          </div>

          {/* Parent/Guardian Information */}
          <div className="card space-y-4">
            <h3 className="font-heading text-xl font-bold dark:text-white light:text-gray-900 uppercase">Parent/Guardian</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Parent's Full Name *</label>
                <input {...register('parentFullName')} className="input-field" />
                {errors.parentFullName && <p className="error-message">{errors.parentFullName.message}</p>}
              </div>
              <div>
                <label className="label">Email *</label>
                <input type="email" {...register('email')} className="input-field" />
                {errors.email && <p className="error-message">{errors.email.message}</p>}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Phone Number *</label>
                <input type="tel" {...register('phone')} className="input-field" />
                {errors.phone && <p className="error-message">{errors.phone.message}</p>}
              </div>
              <div>
                <label className="label">Address</label>
                <input {...register('address')} className="input-field" />
              </div>
            </div>
          </div>

          {/* Emergency Contacts */}
          <div className="card space-y-4">
            <h3 className="font-heading text-xl font-bold dark:text-white light:text-gray-900 uppercase">Emergency Contacts</h3>
            
            <p className="text-sm text-brand-primary font-medium -mb-2">Emergency Contact 1 *</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="label">Name *</label>
                <input {...register('emergency1Name')} className="input-field" />
                {errors.emergency1Name && <p className="error-message">{errors.emergency1Name.message}</p>}
              </div>
              <div>
                <label className="label">Phone *</label>
                <input type="tel" {...register('emergency1Phone')} className="input-field" />
                {errors.emergency1Phone && <p className="error-message">{errors.emergency1Phone.message}</p>}
              </div>
              <div>
                <label className="label">Relationship *</label>
                <input {...register('emergency1Relationship')} className="input-field" placeholder="e.g., Father" />
                {errors.emergency1Relationship && <p className="error-message">{errors.emergency1Relationship.message}</p>}
              </div>
            </div>

            <p className="text-sm dark:text-gray-400 light:text-gray-600 font-medium -mb-2 pt-2">Emergency Contact 2 (optional)</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="label">Name</label>
                <input {...register('emergency2Name')} className="input-field" />
              </div>
              <div>
                <label className="label">Phone</label>
                <input type="tel" {...register('emergency2Phone')} className="input-field" />
              </div>
              <div>
                <label className="label">Relationship</label>
                <input {...register('emergency2Relationship')} className="input-field" placeholder="e.g., Mother" />
              </div>
            </div>

            <div className="pt-2">
              <label className="label">Authorised Collectors</label>
              <p className="dark:text-gray-500 light:text-gray-500 text-xs mb-2">People authorized to collect your child(ren)</p>
              <textarea {...register('authorisedCollectors')} className="input-field resize-none" rows={2} />
            </div>
          </div>

          {/* Pricing Items */}
          {campPricingItems.length > 0 && (
            <div className="card">
              <h3 className="font-heading text-xl font-bold dark:text-white light:text-gray-900 uppercase mb-4">
                Pricing Options
              </h3>
              <p className="dark:text-gray-400 light:text-gray-600 text-sm mb-4">
                Prices are per child × {fields.length} {fields.length === 1 ? 'child' : 'children'}
              </p>
              <div className="space-y-2">
                {campPricingItems.map((item) => (
                  <label
                    key={item.id}
                    className={`flex items-start gap-3 p-3 rounded border transition-all ${
                      item.isRequired 
                        ? 'border-brand-primary/30 bg-brand-primary/5 cursor-default' 
                        : 'dark:border-gray-700 light:border-gray-300 hover:border-brand-primary/50 cursor-pointer'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!!selectedItems[item.id]}
                      disabled={item.isRequired}
                      onChange={(e) => {
                        if (!item.isRequired) {
                          setSelectedItems({ ...selectedItems, [item.id]: e.target.checked })
                        }
                      }}
                      className={`mt-1 ${item.isRequired ? 'opacity-60' : ''}`}
                    />
                    <div className="flex-1">
                      <div className="font-medium dark:text-white light:text-gray-900 flex items-center gap-2">
                        {item.name}
                        {item.isRequired && (
                          <span className="text-xs px-2 py-0.5 rounded bg-brand-primary/20 text-brand-primary">
                            Required
                          </span>
                        )}
                      </div>
                      <div className="text-sm dark:text-gray-400 light:text-gray-600">{item.description}</div>
                    </div>
                    <div className={`font-bold ${item.amount < 0 ? 'text-green-400' : 'dark:text-white light:text-gray-900'}`}>
                      {item.amount < 0 ? '-' : ''}AED {Math.abs(item.amount).toFixed(2)}
                    </div>
                  </label>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t dark:border-gray-700 light:border-gray-300 space-y-2">
                {siblingDiscount > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="dark:text-gray-400 light:text-gray-600">
                      Subtotal ({fields.length} children):
                    </span>
                    <span className="dark:text-gray-400 light:text-gray-600">
                      AED {(totalAmount + siblingDiscount).toFixed(2)}
                    </span>
                  </div>
                )}
                {siblingDiscount > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-green-400 flex items-center gap-2">
                      🎉 Sibling Discount ({fields.length - 1} {fields.length === 2 ? 'sibling' : 'siblings'}):
                    </span>
                    <span className="text-green-400 font-medium">
                      -AED {siblingDiscount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2">
                  <span className="dark:text-white light:text-gray-900 font-medium">
                    Total:
                  </span>
                  <span className="font-heading text-2xl font-bold text-brand-primary">
                    AED {totalAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Permissions */}
          <div className="card space-y-3">
            <h3 className="font-heading text-xl font-bold dark:text-white light:text-gray-900 uppercase mb-2">
              Permissions & Consent
            </h3>
            <p className="dark:text-gray-400 light:text-gray-600 text-sm mb-4">
              These permissions apply to all registered children
            </p>
            
            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" {...register('permissionPhotos')} className="mt-1" />
              <span className="dark:text-gray-300 light:text-gray-700">I give permission for my child(ren) to be photographed/videoed *</span>
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" {...register('permissionHealth')} className="mt-1" />
              <span className="dark:text-gray-300 light:text-gray-700">I authorize staff to seek emergency medical treatment if required *</span>
            </label>
            {errors.permissionHealth && <p className="error-message">{errors.permissionHealth.message}</p>}

            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" {...register('permissionActivities')} className="mt-1" />
              <span className="dark:text-gray-300 light:text-gray-700">I give permission for my child(ren) to participate in all camp activities *</span>
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" {...register('permissionLocations')} className="mt-1" />
              <span className="dark:text-gray-300 light:text-gray-700">I give permission for my child(ren) to visit off-site locations with supervision *</span>
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" {...register('permissionMeals')} className="mt-1" />
              <span className="dark:text-gray-300 light:text-gray-700">I give permission for my child(ren) to have snacks and meals provided *</span>
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" {...register('permissionBathroom')} className="mt-1" />
              <span className="dark:text-gray-300 light:text-gray-700">I give permission for my child(ren) to use bathroom facilities independently *</span>
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" {...register('permissionFirstAid')} className="mt-1" />
              <span className="dark:text-gray-300 light:text-gray-700">I give permission for first aid to be administered if needed *</span>
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" {...register('permissionEquipment')} className="mt-1" />
              <span className="dark:text-gray-300 light:text-gray-700">I give permission for my child(ren) to use sports equipment and facilities *</span>
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" {...register('permissionAppWaiver')} className="mt-1" />
              <span className="dark:text-gray-300 light:text-gray-700">I acknowledge and accept the camp terms and conditions *</span>
            </label>
            {errors.permissionAppWaiver && <p className="error-message">{errors.permissionAppWaiver.message}</p>}
          </div>

          {selectedCamp?.registrationStatus === 'paused' ? (
            <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4 text-center">
              <span className="text-yellow-400 font-bold">⏸️ Registration is currently paused</span>
            </div>
          ) : selectedCamp?.registrationStatus === 'closed' ? (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-center">
              <span className="text-red-400 font-bold">🔒 Registration is closed</span>
            </div>
          ) : (
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full py-4 text-lg"
            >
              {isSubmitting 
                ? 'Processing...' 
                : selectedCamp && selectedCamp.maxSpots - selectedCamp.spotsTaken <= 0 && selectedCamp.waitlistEnabled
                  ? '📋 Join Waitlist'
                  : totalAmount === 0 
                    ? 'Complete Registration - Free'
                    : `Proceed to Payment - AED ${totalAmount.toFixed(2)}`
              }
            </button>
          )}
        </form>
      )}
    </div>
  )
}

// Child Section Component
function ChildSection({
  index,
  register,
  errors,
  watch,
  onRemove,
  canRemove,
}: {
  index: number
  register: any
  errors: any
  watch: any
  onRemove: () => void
  canRemove: boolean
}) {
  const hasMedical = watch(`children.${index}.hasMedicalConditions`)
  const hasNeeds = watch(`children.${index}.hasAdditionalNeeds`)
  const hasAllergies = watch(`children.${index}.hasAllergies`)
  const hasMedication = watch(`children.${index}.hasMedication`)
  const hasFurtherInfo = watch(`children.${index}.hasFurtherInfo`)

  return (
    <div className="mb-6 p-4 dark:bg-gray-800/50 light:bg-gray-100 rounded-lg border dark:border-gray-700 light:border-gray-300">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-heading text-lg text-brand-primary">
          Child {index + 1}
        </h4>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-red-400 hover:text-red-300 text-sm"
          >
            Remove
          </button>
        )}
      </div>

      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label">Full Name *</label>
            <input {...register(`children.${index}.childFullName`)} className="input-field" />
            {errors.children?.[index]?.childFullName && (
              <p className="error-message">{errors.children[index].childFullName.message}</p>
            )}
          </div>
          <div>
            <label className="label">Date of Birth *</label>
            <input type="date" {...register(`children.${index}.childDob`)} className="input-field" />
            {errors.children?.[index]?.childDob && (
              <p className="error-message">{errors.children[index].childDob.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="label">Can walk home alone? *</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.walkHomeAlone`)} value="yes" />
              Yes
            </label>
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.walkHomeAlone`)} value="no" />
              No
            </label>
          </div>
        </div>

        {/* Medical Conditions */}
        <div>
          <label className="label">Any medical conditions? *</label>
          <div className="flex gap-4 mb-2">
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.hasMedicalConditions`)} value="yes" />
              Yes
            </label>
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.hasMedicalConditions`)} value="no" />
              No
            </label>
          </div>
          {hasMedical === 'yes' && (
            <textarea
              {...register(`children.${index}.medicalConditionsDetails`)}
              className="input-field resize-none"
              rows={2}
              placeholder="Please provide details..."
            />
          )}
        </div>

        {/* Additional Needs */}
        <div>
          <label className="label">Any additional needs? *</label>
          <div className="flex gap-4 mb-2">
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.hasAdditionalNeeds`)} value="yes" />
              Yes
            </label>
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.hasAdditionalNeeds`)} value="no" />
              No
            </label>
          </div>
          {hasNeeds === 'yes' && (
            <textarea
              {...register(`children.${index}.additionalNeedsDetails`)}
              className="input-field resize-none"
              rows={2}
              placeholder="Please provide details..."
            />
          )}
        </div>

        {/* Allergies */}
        <div>
          <label className="label">Any allergies? *</label>
          <div className="flex gap-4 mb-2">
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.hasAllergies`)} value="yes" />
              Yes
            </label>
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.hasAllergies`)} value="no" />
              No
            </label>
          </div>
          {hasAllergies === 'yes' && (
            <textarea
              {...register(`children.${index}.allergiesDetails`)}
              className="input-field resize-none"
              rows={2}
              placeholder="Please provide details..."
            />
          )}
        </div>

        {/* Medication */}
        <div>
          <label className="label">Taking any medication? *</label>
          <div className="flex gap-4 mb-2">
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.hasMedication`)} value="yes" />
              Yes
            </label>
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.hasMedication`)} value="no" />
              No
            </label>
          </div>
          {hasMedication === 'yes' && (
            <textarea
              {...register(`children.${index}.medicationDetails`)}
              className="input-field resize-none"
              rows={2}
              placeholder="Please provide details..."
            />
          )}
        </div>

        {/* Further Information */}
        <div>
          <label className="label">Any further information? *</label>
          <div className="flex gap-4 mb-2">
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.hasFurtherInfo`)} value="yes" />
              Yes
            </label>
            <label className="flex items-center gap-2 dark:text-white light:text-gray-900 text-sm">
              <input type="radio" {...register(`children.${index}.hasFurtherInfo`)} value="no" />
              No
            </label>
          </div>
          {hasFurtherInfo === 'yes' && (
            <textarea
              {...register(`children.${index}.furtherInfoDetails`)}
              className="input-field resize-none"
              rows={2}
              placeholder="Please provide details..."
            />
          )}
        </div>
      </div>
    </div>
  )
}

// Payment Form Component
function PaymentForm({ 
  totalAmount, 
  childCount,
  onSuccess, 
  onBack 
}: { 
  totalAmount: number
  childCount: number
  onSuccess: () => void
  onBack: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string>('')
  const [processing, setProcessing] = useState(false)

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) return

    setProcessing(true)
    setError('')

    try {
      const { error: submitError } = await elements.submit()
      if (submitError) {
        setError(submitError.message || 'Payment failed')
        setProcessing(false)
        return
      }

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      })

      if (confirmError) {
        setError(confirmError.message || 'Payment failed')
        setProcessing(false)
        return
      }

      // Payment succeeded - submit registration
      const formData = JSON.parse(sessionStorage.getItem('registration_form_data') || '{}')
      
      const response = await fetch('/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          paymentStatus: 'paid',
        }),
      })

      if (!response.ok) {
        throw new Error('Registration failed')
      }

      sessionStorage.removeItem('registration_form_data')
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment processing failed')
      setProcessing(false)
    }
  }

  return (
    <div className="card max-w-2xl mx-auto">
      <div className="mb-6">
        <button onClick={onBack} className="text-brand-primary hover:underline text-sm">
          ← Back to form
        </button>
      </div>

      <h2 className="font-heading text-2xl font-bold dark:text-white light:text-gray-900 uppercase mb-6">
        Complete Payment
      </h2>

      <div className="dark:bg-gray-800/50 light:bg-gray-100 p-4 rounded mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="dark:text-gray-400 light:text-gray-600">Registering:</span>
          <span className="dark:text-white light:text-gray-900">{childCount} {childCount === 1 ? 'child' : 'children'}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="dark:text-gray-400 light:text-gray-600">Total Amount:</span>
          <span className="text-2xl font-bold text-brand-primary">AED {totalAmount.toFixed(2)}</span>
        </div>
      </div>

      <form onSubmit={handlePaymentSubmit}>
        <div className="mb-6">
          <PaymentElement />
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-600 rounded p-3 mb-4 text-red-200">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!stripe || processing}
          className="btn-primary w-full py-4 text-lg"
        >
          {processing ? 'Processing...' : `Pay AED ${totalAmount.toFixed(2)}`}
        </button>
      </form>

      <p className="dark:text-gray-400 light:text-gray-600 text-xs text-center mt-4">
        Payments are securely processed by Stripe
      </p>
    </div>
  )
}
